import { Pause, Play, RotateCcw } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import type { Trajectory, WallPanel } from "@/lib/api";
import { cn } from "@/lib/utils";

/** Playback multipliers. 1x plays back in the recording's own real time. */
const SPEEDS = [1, 5, 10] as const;

/**
 * Wall colour by panel model_type (column 0 of a wall panel).
 *
 * Types 1–4 are the straight / corner / tee / cross pieces emitted by maze.py.
 * Tinting them apart makes the maze legible as structure rather than a blob.
 */
const WALL_COLORS: Record<number, string> = {
  1: "rgba(160,160,160,0.70)",
  2: "rgba(80,80,80,0.80)",
  3: "rgba(139,90,43,0.80)",
  4: "rgba(200,200,200,0.60)",
};
const WALL_FALLBACK = "rgba(120,120,120,0.5)";

const EXPLORATION_RGB = "100,149,237"; // cornflower blue
const NAVIGATION_RGB = "220,50,50";
const TARGET_COLOR = "#22c55e";

const PAD = 24;

/**
 * Length of the fading trail behind the robot, in samples.
 *
 * The original used a fixed 300, tuned for full-resolution recordings of tens of
 * thousands of samples. Since the server can now downsample, a fixed count would
 * mean the trail covers wildly different fractions of the path depending on
 * `max_points`, so it scales with the series instead — 2%, clamped to something
 * visible at both extremes.
 */
function trailLength(total: number): number {
  return Math.min(400, Math.max(60, Math.round(total * 0.02)));
}

interface Bounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

/**
 * Animated top-down playback of a recorded run over the maze.
 *
 * Canvas rather than SVG: a recording carries thousands of samples and the trail
 * is redrawn every frame, which as DOM nodes would stall the page.
 *
 * The Y axis is flipped so the view matches the simulator's top-down camera —
 * PyBullet is Z-up with Y increasing away from the origin, whereas canvas Y grows
 * downward.
 */
export function TrajectoryPlayer({
  walls = [],
  trajectory,
  className,
  /**
   * CSS length capping the square plot, so the transport controls below it stay
   * on screen. The default reserves room for the page chrome; inside a dialog
   * pass something tighter, because the dialog itself is already inset.
   */
  maxSize = "calc(100svh - 300px)",
  /**
   * What `t` in the sample stream means. A client recording carries wall-clock
   * seconds; a server-simulated one carries the step index, so "t = 4.00s" would
   * be a lie there. Required, not defaulted: the default was "seconds", and the
   * admin trajectory dialog forgot to pass anything, so it labelled every
   * server run's step count as seconds.
   */
  timeUnit,
}: {
  walls?: WallPanel[];
  trajectory: Trajectory;
  className?: string;
  maxSize?: string;
  timeUnit: "seconds" | "steps";
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const startedAtRef = useRef(0);

  const points = trajectory.points;
  const lastIndex = Math.max(points.length - 1, 0);

  // Open on the finished run, not an empty maze: the reader came to see where it
  // ended up. Play rewinds and replays from the start.
  const [playing, setPlaying] = useState(false);
  const [frame, setFrame] = useState(lastIndex);
  const [speedIndex, setSpeedIndex] = useState(0);
  const trail = useMemo(() => trailLength(points.length), [points.length]);

  const bounds = useMemo<Bounds>(() => {
    const xs = [...walls.map((w) => w[2]), ...points.map((p) => p.x), trajectory.target.x];
    const ys = [...walls.map((w) => w[3]), ...points.map((p) => p.y), trajectory.target.y];
    if (!xs.length) return { minX: 0, maxX: 1, minY: 0, maxY: 1 };
    return {
      minX: Math.min(...xs),
      maxX: Math.max(...xs),
      minY: Math.min(...ys),
      maxY: Math.max(...ys),
    };
  }, [walls, points, trajectory.target]);

  const draw = useCallback(
    (upTo: number) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const dpr = window.devicePixelRatio || 1;
      const width = canvas.width / dpr;
      const height = canvas.height / dpr;

      const spanX = bounds.maxX - bounds.minX || 1;
      const spanY = bounds.maxY - bounds.minY || 1;
      const scale = Math.min((width - PAD * 2) / spanX, (height - PAD * 2) / spanY);
      const px = (x: number) => PAD + (x - bounds.minX) * scale;
      const py = (y: number) => height - PAD - (y - bounds.minY) * scale;

      ctx.save();
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, width, height);

      // --- maze ---
      for (const panel of walls) {
        ctx.fillStyle = WALL_COLORS[panel[0]] ?? WALL_FALLBACK;
        ctx.beginPath();
        ctx.arc(px(panel[2]), py(panel[3]), 3, 0, Math.PI * 2);
        ctx.fill();
      }

      // --- whole path, faint ---
      // So the route is legible at any scrub position, not only as the trail
      // passes over it. Drawn as one stroke per phase so the colours stay apart.
      if (points.length > 1) {
        ctx.lineWidth = 1.5;
        ctx.lineJoin = "round";
        let phase = points[0].phase;
        ctx.strokeStyle = `rgba(${phase === "exploration" ? EXPLORATION_RGB : NAVIGATION_RGB},0.28)`;
        ctx.beginPath();
        ctx.moveTo(px(points[0].x), py(points[0].y));
        for (let i = 1; i < points.length; i++) {
          const point = points[i];
          if (point.phase !== phase) {
            ctx.stroke();
            phase = point.phase;
            ctx.strokeStyle = `rgba(${phase === "exploration" ? EXPLORATION_RGB : NAVIGATION_RGB},0.28)`;
            ctx.beginPath();
            ctx.moveTo(px(points[i - 1].x), py(points[i - 1].y));
          }
          ctx.lineTo(px(point.x), py(point.y));
        }
        ctx.stroke();
      }

      // --- fading trail ---
      const from = Math.max(0, upTo - trail);
      for (let i = from; i <= upTo; i++) {
        const point = points[i];
        if (!point) continue;
        const alpha = (i - from) / trail;
        const rgb = point.phase === "exploration" ? EXPLORATION_RGB : NAVIGATION_RGB;
        ctx.fillStyle = `rgba(${rgb},${alpha})`;
        ctx.beginPath();
        ctx.arc(px(point.x), py(point.y), 2, 0, Math.PI * 2);
        ctx.fill();
      }

      const first = points[0];
      const last = points[lastIndex];
      const tx = px(trajectory.target.x);
      const ty = py(trajectory.target.y);

      // --- finish -> target ---
      // The gap that was scored. Dashed so it reads as a measurement, not a path.
      if (last) {
        ctx.strokeStyle = "rgba(120,120,120,0.7)";
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 4]);
        ctx.beginPath();
        ctx.moveTo(px(last.x), py(last.y));
        ctx.lineTo(tx, ty);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      // --- start ---
      if (first) {
        ctx.strokeStyle = "rgba(60,60,60,0.9)";
        ctx.fillStyle = "rgba(255,255,255,0.9)";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(px(first.x), py(first.y), 4.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }

      // --- finish ---
      if (last) {
        ctx.strokeStyle = `rgb(${NAVIGATION_RGB})`;
        ctx.lineWidth = 1.5;
        const fx = px(last.x);
        const fy = py(last.y);
        ctx.beginPath();
        ctx.moveTo(fx - 4, fy - 4);
        ctx.lineTo(fx + 4, fy + 4);
        ctx.moveTo(fx + 4, fy - 4);
        ctx.lineTo(fx - 4, fy + 4);
        ctx.stroke();
      }

      // --- robot ---
      const current = points[upTo];
      if (current) {
        ctx.fillStyle = `rgb(${
          current.phase === "exploration" ? EXPLORATION_RGB : NAVIGATION_RGB
        })`;
        ctx.strokeStyle = "rgba(255,255,255,0.9)";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(px(current.x), py(current.y), 5, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }

      // --- target ---
      ctx.fillStyle = TARGET_COLOR;
      ctx.font = "18px serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("★", tx, ty);

      ctx.restore();
    },
    [bounds, walls, points, lastIndex, trail, trajectory.target],
  );

  // Keep the latest draw and frame reachable from the ResizeObserver without
  // making it a dependency, which would re-subscribe on every frame.
  const drawRef = useRef(draw);
  const frameRef = useRef(frame);
  useEffect(() => {
    drawRef.current = draw;
  }, [draw]);
  useEffect(() => {
    frameRef.current = frame;
  }, [frame]);

  // Size the backing store to the element's real pixel size, then redraw.
  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.round(entry.contentRect.width * dpr);
      canvas.height = Math.round(entry.contentRect.height * dpr);
      drawRef.current(frameRef.current);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Playback. Duration comes from the recording's own timestamps, so 1x is real
  // time regardless of how many samples the server returned.
  useEffect(() => {
    if (!playing || points.length < 2) return;

    const speed = SPEEDS[speedIndex];
    const t0 = points[0]?.t ?? 0;
    const t1 = points[points.length - 1]?.t ?? 0;
    // A client recording's `t` is seconds; a server run's is the step index, and a step
    // is 10 ms of simulated time (`sim_ms = steps * 10`). Without this, a 1,500-step run
    // would take 25 minutes to replay at 1x.
    const msPerUnit = timeUnit === "steps" ? 10 : 1000;
    const durationMs = Math.max(((t1 - t0) * msPerUnit) / speed, 1);

    const step = (timestamp: number) => {
      if (!startedAtRef.current) startedAtRef.current = timestamp;
      const elapsed = timestamp - startedAtRef.current;
      const index = Math.min(
        Math.floor((elapsed / durationMs) * points.length),
        lastIndex,
      );
      setFrame(index);
      drawRef.current(index);
      if (index < lastIndex) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        setPlaying(false);
      }
    };

    startedAtRef.current = 0;
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [playing, speedIndex, points, lastIndex, timeUnit]);

  // Redraw on scrub while paused.
  useEffect(() => {
    if (!playing) drawRef.current(frame);
  }, [frame, playing]);

  const restart = () => {
    setFrame(0);
    startedAtRef.current = 0;
    setPlaying(true);
  };

  const togglePlay = () => {
    if (playing) {
      setPlaying(false);
      return;
    }
    if (frame >= lastIndex) setFrame(0);
    startedAtRef.current = 0;
    setPlaying(true);
  };

  const current = points[frame];
  const t0 = points[0]?.t ?? 0;
  const elapsed = (current?.t ?? t0) - t0;
  const atEnd = frame >= lastIndex;

  return (
    <div className={cn("space-y-3", className)}>
      <div
        ref={containerRef}
        className="bg-muted w-full rounded-lg border"
        style={{ aspectRatio: "1 / 1", maxWidth: `min(100%, ${maxSize})` }}
      >
        <canvas ref={canvasRef} className="h-full w-full" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={togglePlay} className="w-20">
          {playing ? (
            <>
              <Pause className="size-3.5" aria-hidden />
              Pause
            </>
          ) : (
            <>
              <Play className="size-3.5" aria-hidden />
              {atEnd ? "Replay" : "Play"}
            </>
          )}
        </Button>

        <Button
          size="icon-sm"
          variant="ghost"
          onClick={restart}
          aria-label="Restart"
          disabled={frame === 0 && !playing}
        >
          <RotateCcw className="size-3.5" aria-hidden />
        </Button>

        <div className="flex gap-1" role="group" aria-label="Playback speed">
          {SPEEDS.map((speed, index) => (
            <Button
              key={speed}
              size="sm"
              variant={index === speedIndex ? "default" : "outline"}
              onClick={() => setSpeedIndex(index)}
              className="px-2 text-xs"
              aria-pressed={index === speedIndex}
            >
              {speed}×
            </Button>
          ))}
        </div>

        <Badge variant="outline" className="font-mono text-xs capitalize">
          {current?.phase ?? "—"}
        </Badge>
        <span className="text-muted-foreground font-mono text-xs">
          {timeUnit === "steps"
            ? `step ${Math.round(current?.t ?? t0)}`
            : `t = ${elapsed.toFixed(2)}s`}
        </span>
        <span className="text-muted-foreground ml-auto font-mono text-xs">
          {frame + 1}/{points.length}
          {trajectory.total_points > points.length
            ? ` of ${trajectory.total_points.toLocaleString()}`
            : ""}
        </span>
      </div>

      <Slider
        min={0}
        max={lastIndex}
        step={1}
        value={[frame]}
        onValueChange={(value) => {
          // Scrubbing takes over from playback rather than fighting it.
          setPlaying(false);
          const next = Array.isArray(value) ? value[0] : value;
          if (typeof next === "number") setFrame(next);
        }}
        aria-label="Playback position"
        className="w-full"
      />

      <div className="text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <Legend color={`rgb(${EXPLORATION_RGB})`} label="Exploration" />
        <Legend color={`rgb(${NAVIGATION_RGB})`} label="Navigation" />
        <span className="flex items-center gap-1.5">
          <span
            className="size-2.5 rounded-full border-[1.5px] border-neutral-700 bg-white"
            aria-hidden
          />
          Start
        </span>
        <span className="flex items-center gap-1.5">
          <span className="text-[13px] leading-none font-bold text-[rgb(220,50,50)]" aria-hidden>
            ×
          </span>
          Finish
        </span>
        <Legend color={TARGET_COLOR} label="Target" />
      </div>
    </div>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <span
        className="size-2.5 rounded-full"
        style={{ backgroundColor: color }}
        aria-hidden
      />
      {label}
    </span>
  );
}
